import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from '@tanstack/react-query';
import { cloudConnectSubscriptionApi } from '../endpoints';
import { requestToCancelEnterpriseSubscription } from '../endpoints/cloudConnectSubscriptionApi';
import { queryKeys } from './queryKeys';

export const enterpriseSubscriptionOptions = () =>
  queryOptions({
    queryKey: queryKeys.enterpriseSubscriptions.getSubscription,
    queryFn: () => cloudConnectSubscriptionApi.getEnterpriseSubscription(),
  });

export const useEnterpriseSubscription = () =>
  useSuspenseQuery(enterpriseSubscriptionOptions());

export const useEnterpriseSubscriptionQuery = (config: { enabled: boolean }) =>
  useQuery({
    ...enterpriseSubscriptionOptions(),
    retry: 0,
    enabled: config.enabled,
    staleTime: 60_000, // 1 minute
  });

const createBillingPortalSessionOptions = () =>
  queryOptions({
    queryKey: queryKeys.enterpriseSubscriptions.getBillingPortalSession,
    queryFn: () => cloudConnectSubscriptionApi.createBillingPortalSession(),
  });

export const useCreateBillingPortalSession = () =>
  useQuery(createBillingPortalSessionOptions());

export const useRequestToCancelEnterpriseSubscription = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: requestToCancelEnterpriseSubscription,
    onSettled: () => {
      return queryClient.invalidateQueries({
        queryKey: queryKeys.enterpriseSubscriptions.getSubscription,
      });
    },
  });
};

export const useDeleteRequestToCancelEnterpriseSubscription = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn:
      cloudConnectSubscriptionApi.deleteRequestToCancelEnterpriseSubscription,
    onSettled: () => {
      return queryClient.invalidateQueries({
        queryKey: queryKeys.enterpriseSubscriptions.getSubscription,
      });
    },
  });
};

export const useChangeEnterpriseSubscriptionBillingInterval = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (vars: {
      interval: 'monthly' | 'annual';
      seats: number | null | undefined;
    }) => {
      if (vars.interval === 'annual' && !vars.seats) {
        throw new Error('Seats amount is required');
      }

      return cloudConnectSubscriptionApi.changeEnterpriseSubscriptionBillingInterval(
        {
          interval: vars.interval,
          seats: vars.seats ?? undefined,
        }
      );
    },
    onSuccess() {
      return queryClient.invalidateQueries({
        queryKey: queryKeys.enterpriseSubscriptions.getSubscription,
      });
    },
  });
};
